import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ChevronDownIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import { createPortal } from 'react-dom';
import { selectIsAuthenticated } from '../store/slices/authSlice';
import { toast } from 'react-hot-toast';

const WarehouseSelector = ({ size = 'md', className = '' }) => {
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasDefaultWarehouse, setHasDefaultWarehouse] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });
  const isAuthenticated = useSelector(selectIsAuthenticated);

  // Fetch available warehouses
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const response = await fetch('/api/user-preferences/warehouses');
        if (response.ok) {
          const data = await response.json();
          setWarehouses(data.warehouses || []);
        }
      } catch (error) {
        console.error('Error fetching warehouses:', error);
      }
    };

    fetchWarehouses();
  }, []);

  // Fetch user's default warehouse if authenticated; otherwise load guest selection
  useEffect(() => {
    const fetchUserDefaultWarehouse = async () => {
      if (!isAuthenticated) {
        // Load guest selection from localStorage
        try {
          const raw = localStorage.getItem('guest_warehouse');
          if (raw) {
            const parsed = JSON.parse(raw);
            setSelectedWarehouse(parsed);
          } else {
            setSelectedWarehouse(null);
          }
        } catch {
          setSelectedWarehouse(null);
        }
        return;
      }

      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch('/api/user-preferences/default-warehouse', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          // If user has a preset default branch, show it by default
          if (data.default_warehouse) {
            setSelectedWarehouse(data.default_warehouse);
            // Clear any prior temporary view selection so default takes effect
            try { localStorage.removeItem('view_warehouse'); } catch {}
          } else {
            // No preset default: prefer any existing temporary view selection
            try {
              const viewRaw = localStorage.getItem('view_warehouse');
              if (viewRaw) {
                setSelectedWarehouse(JSON.parse(viewRaw));
              } else {
                setSelectedWarehouse(null);
              }
            } catch {
              setSelectedWarehouse(null);
            }
          }
          setHasDefaultWarehouse(!!data.default_warehouse_id);
        }
      } catch (error) {
        console.error('Error fetching user default warehouse:', error);
      }
    };

    fetchUserDefaultWarehouse();
  }, [isAuthenticated]);

  const handleWarehouseChange = async (warehouse) => {
    if (!isAuthenticated) {
      // Guest: allow free selection and persist locally
      setSelectedWarehouse(warehouse);
      try {
        localStorage.setItem('guest_warehouse', JSON.stringify(warehouse));
        // Also set unified key used elsewhere
        localStorage.setItem('view_warehouse', JSON.stringify(warehouse));
      } catch {}
      window.dispatchEvent(new CustomEvent('warehouse:changed', { detail: warehouse }));
      toast.success(`Branch set to ${warehouse.name}`);
      // Important: force refetch across the app so availability uses view_warehouse_id
      window.location.reload();
      return;
    }

    // Authenticated: selection is a temporary viewing branch, not default
    setLoading(true);
    try {
      setSelectedWarehouse(warehouse);
      try {
        localStorage.setItem('view_warehouse', JSON.stringify(warehouse));
      } catch {}
      window.dispatchEvent(new CustomEvent('warehouse:changed', { detail: warehouse }));
      toast.success(`Viewing branch set to ${warehouse.name}`);
      // Refresh so all product queries honor view_warehouse_id
      window.location.reload();
    } catch (error) {
      console.error('Error setting viewing warehouse:', error);
      toast.error('Failed to change viewing branch');
    } finally {
      setLoading(false);
    }
  };

  const buttonSizeClasses = size === 'sm'
    ? 'px-2.5 py-1.5 text-xs'
    : 'px-3 py-2 text-sm';

  const labelMaxWidth = size === 'sm' ? 'sm:max-w-40' : 'sm:max-w-48';

  const updateMenuPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left,
        width: rect.width
      });
    }
  };

  const handleToggle = () => {
    updateMenuPosition();
    setIsOpen(!isOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelectWarehouse = async (warehouse) => {
    setIsOpen(false);
    await handleWarehouseChange(warehouse);
  };

  return (
    <div className={`relative block sm:inline-block text-left w-full ${className}`}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        type="button"
        className={`inline-flex items-center justify-between w-full rounded-md border shadow-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 bg-white text-gray-700 border-gray-300 hover:bg-gray-50 focus:ring-primary-500 ${buttonSizeClasses}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="flex items-center min-w-0">
          <BuildingStorefrontIcon className="h-4 w-4 mr-2 shrink-0" />
          <span className={`truncate sm:truncate max-w-full ${labelMaxWidth}`}>
            {selectedWarehouse ? selectedWarehouse.name : 'Branch'}
          </span>
        </span>
        <ChevronDownIcon className="h-4 w-4 ml-2 hidden sm:inline-block shrink-0" />
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[200] origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          style={{
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
            width: `${Math.max(menuPosition.width, size === 'sm' ? 220 : 280)}px`,
            maxWidth: 'calc(100vw - 32px)'
          }}
          role="listbox"
        >
          <div className="py-1 max-h-80 overflow-y-auto">
            <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 sticky top-0 bg-white z-10">
              {isAuthenticated && hasDefaultWarehouse ? 'Your Branch (Default)' : 'Select Your Branch'}
            </div>
            {warehouses.map((warehouse) => (
              <button
                key={warehouse.id}
                onClick={() => handleSelectWarehouse(warehouse)}
                disabled={loading}
                className={`${
                  selectedWarehouse?.id === warehouse.id ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                } group flex w-full items-center px-4 py-2 text-sm disabled:opacity-50 text-left hover:bg-gray-100 hover:text-gray-900`}
              >
                <BuildingStorefrontIcon className="h-4 w-4 mr-3 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{warehouse.name}</div>
                  {warehouse.address && (
                    <div className="text-xs text-gray-500 truncate">{warehouse.address}</div>
                  )}
                </div>
                {selectedWarehouse?.id === warehouse.id && (
                  <div className="ml-2 h-2 w-2 bg-primary-600 rounded-full shrink-0"></div>
                )}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default WarehouseSelector;
